import { useEffect, useRef } from 'react';
import ReactMarkdown from 'react-markdown';

interface MarkdownRendererProps {
  content: string;
  className?: string;
  placeholder?: string;
  getTableOfContent?(tableOfContent: string[]): void;
  getContainerRef?(ref: HTMLDivElement | null): void;
}

function cn(...classes: (string | undefined | false | null)[]) {
  return classes.filter(Boolean).join(' ');
}

export const generateHeaderId = (text: string): string =>
  text
    .toLowerCase()
    .replace(/[^\w\s-]/g, '')
    .replace(/\s+/g, '-')
    .trim();

function extractHeaders(markdown: string): string[] {
  const headerRegex = /^#{1,6}\s+(.+)$/gm;
  const headers: string[] = [];
  let match: RegExpExecArray | null;

  while ((match = headerRegex.exec(markdown)) !== null) {
    const headerText = match[1]?.trim() || '';
    const cleanText = headerText
      .replace(/\*\*(.*?)\*\*/g, '$1')
      .replace(/\*(.*?)\*/g, '$1')
      .replace(/`(.*?)`/g, '$1')
      .replace(/\[(.*?)\]\(.*?\)/g, '$1')
      .trim();

    if (cleanText && cleanText !== '**' && cleanText !== '*' && !cleanText.match(/^[_\-\s*#]+$/) && cleanText.length > 0) {
      headers.push(cleanText);
    }
  }

  return headers;
}

export const MarkdownRenderer = ({ content, className, placeholder = 'No content', getTableOfContent, getContainerRef }: MarkdownRendererProps) => {
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (getContainerRef) {
      getContainerRef(containerRef.current);
    }
  }, [getContainerRef]);

  useEffect(() => {
    if (getTableOfContent && content) {
      const headers = extractHeaders(content);
      getTableOfContent(headers);
    }
  }, [content, getTableOfContent]);

  // Show placeholder if no content
  if (!content || !content.trim()) {
    return <p className={cn('text-gray-400 italic', className)}>{placeholder}</p>;
  }

  return (
    <div
      ref={containerRef}
      className={cn('prose prose-sm max-w-none', className)}
      style={{
        padding: '20px',
      }}
    >
      <ReactMarkdown
        components={{
          // Customize paragraph spacing
          p: ({ children }) => <p className="mb-2 last:mb-0">{children}</p>,

          // Ensure strong/em elements have proper styling
          strong: ({ children }) => <strong className="font-semibold">{children}</strong>,
          em: ({ children }) => <em className="italic">{children}</em>,

          // Handle line breaks properly
          br: () => <br />,

          // Style headings if used
          h1: ({ children }) => {
            const text = typeof children === 'string' ? children : String(children);
            const id = generateHeaderId(text);
            return (
              <h1 id={id} className="text-lg font-semibold mb-2">
                {children}
              </h1>
            );
          },
          h2: ({ children }) => {
            const text = typeof children === 'string' ? children : String(children);
            const id = generateHeaderId(text);
            return (
              <h2 id={id} className="text-base font-semibold mb-2">
                {children}
              </h2>
            );
          },
          h3: ({ children }) => {
            const text = typeof children === 'string' ? children : String(children);
            const id = generateHeaderId(text);
            return (
              <h3 id={id} className="text-sm font-semibold mb-2">
                {children}
              </h3>
            );
          },

          // Style lists
          ul: ({ children }) => <ul className="list-disc list-inside mb-2">{children}</ul>,
          ol: ({ children }) => <ol className="list-decimal list-inside mb-2">{children}</ol>,
          li: ({ children }) => <li className="mb-1">{children}</li>,
        }}
      >
        {content}
      </ReactMarkdown>
    </div>
  );
};
